import { EntityFileList } from '@/common/entities/entityFile/EntityFileList';
import { EntityDetail } from '@/common/entityFramework/entityDetail/EntityDetail';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { ValList } from '@/common/val/ValList';
import { JobList } from '@/skdr/entities/job/JobList';
import { useCallback, useEffect, useState } from 'react';
import { CalcProdList } from '../calcProd/CalcProdList';
import { CalcForm } from './CalcForm';
import { CalcDepList } from './calcDep/CalcDepList';
import { ReallocRatioList } from './reallocRatio/ReallocRatioList';

/**
 * Per requirement GP-9, this provides details for a calculation, including a view of its Transaction Overrides (GP-205) and its Transaction Value Overrides (GP-221)
 **/
export const CalcDetail = () => {
  const { entityObj } = useEntityMgr();
  const rsrc = useRsrc();
  const { hasRole } = usePrincipal();
  const [chainId, setChainId] = useState();

  const augTrxOvrdRowsReq = useCallback(
    (rowsReq) => {
      if (!entityObj) return;
      rowsReq.expr = ExprUtils.and(rowsReq.expr, ExprUtils.whereParams('calcId', entityObj.id));
    },
    [entityObj],
  );

  useEffect(() => {
    // TODO: Confirm permission issue
    const hasJobRole = hasRole('SKDR_VIEW');
    if (!hasJobRole) {
      return;
    }

    if (!entityObj.id) return;

    rsrc.rows('skdr', 'RUN', { expr: ExprUtils.whereParams('id', entityObj.ctxRunId) }).then((ctxRun) => {
      const jobId = ctxRun[0].jobId;
      const rowsReq = {
        replaceParams: { childJobId: jobId },
      };
      rsrc.rows('skdr', 'CHAIN_FROM_CHILD_JOB', rowsReq).then((res) => {
        setChainId(res[0]?.id);
      });
    });
  }, [entityObj]);

  const tabSpecs = [
    { id: 'infoTab', label: 'Info', content: <CalcForm /> },
    { id: 'prodsTab', label: 'Products', content: <CalcProdList calcObj={entityObj} /> },
  ];

  if (entityObj.polType === 'BUNDLE') {
    tabSpecs.push({
      id: 'reallocRatioTab',
      label: 'Reallocation Ratios',
      content: <ReallocRatioList calcObj={entityObj} />,
    });
  }

  if (!['EST_BPI', 'EST_BPA'].includes(entityObj?.type)) {
    tabSpecs.push(
      { id: 'depsTab', label: 'Dependencies', content: <CalcDepList calcObj={entityObj} /> },
      {
        id: 'trxOvrdTab',
        label: 'Transaction Inc/Exc Overrides',
        content: (
          <EntityList
            entity={'CALC_TRX_OVRD'}
            augRowsReq={augTrxOvrdRowsReq}
            gridOptions={{ getContextMenuItems: {} }}
          />
        ),
      },
      {
        id: 'trxValTab',
        label: 'Transaction Value Overrides',
        content: (
          <EntityList
            entity={'CALC_TRX_VALUE_OVRD'}
            augRowsReq={augTrxOvrdRowsReq}
            augTabButtons={(buttons) => {
              ObjUtils.clearObj(buttons);
            }}
            gridOptions={{ getContextMenuItems: {} }}
          />
        ),
      },
    );
  }

  tabSpecs.push(
    { id: 'valTab', label: 'Validations', content: <ValList parentEntity="CALC" parentEntityId={entityObj.id} /> },
    { id: 'jobTab', label: 'Job Chain List', disabled: !chainId, content: <JobList chain jobId={chainId} /> },
    {
      id: 'soxControlsTab',
      label: 'SOX Controls',
      content: <EntityFileList parentEntity="CALC" parentEntityId={entityObj.id} />,
    },
    {
      id: 'approvalsTab',
      label: 'Approval Routes',
      disabled: !entityObj.apprJobId,
      content: <JobList chain jobId={entityObj.apprJobId} />,
    },
  );

  return <EntityDetail key={entityObj} tabSpecs={tabSpecs} />;
};
